import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import "./style.css";
import {
  Button,
  Divider,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Radio,
  Segmented,
  Select,
  Spin,
  Switch,
  Upload,
  message,
} from "antd";
import {
  CameraOutlined,
  CloseOutlined,
  LoadingOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import {
  formatPhoneNumber,
  GENDER,
  MODE_PREFERENCE,
  phoneNumberPattern,
  PROFILE_MENUS,
} from "utils/constants";
import { MainStore } from "core/store";
import Webcam from "react-webcam";
import BGImage from "../../assets/images/png/bg.png";

import AlertError from "./alert-error";
import imageCompression from "browser-image-compression";
import WarningIcon from "assets/images/png/warning-icon.png";
import VerifyIcon from "assets/images/png/verify_icon.png";
import DefaultGolfCourseImage from "assets/images/png/defaulf-golf-course-image.png";
import MessagePopup from "components/messagePopup";
import liff from "@line/liff";

import api from "api";

const { TextArea } = Input;
const { Option } = Select;

export default function Profile({ user_type, user_profile, user_data }) {
  // Loading states
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);

  // Profile data states
  const [profileFile, setProfileFile] = useState([]);
  const [showProfile, setShowProfile] = useState(
    user_data.is_show_profile_image
  );
  const [fileList, setFileList] = useState([]);
  const [identityFile, setIdentityFile] = useState(null);
  const [submitData, setSubmitData] = useState({});

  // UI states
  const [messageApi, contextHolder] = message.useMessage();
  const [isClosed, setIsClosed] = useState(false);
  const [isReValidateOpen, setIsReValidateOpen] = useState(false);
  const [isVerifyOpen, setIsVerifyOpen] = useState(false);
  const [errorVerifyList, setErrorVerifyList] = useState([]);
  const [isOpenVerifyError, setIsOpenVerifyError] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState("");

  // Data states
  const [controllerData, setControllerData] = useState(null);
  const [selectedBankName, setSelectedBankName] = useState("");
  const [isSelectedBank, setIsSelectedBank] = useState(null);
  const [golfCourseList, setGolfCourseList] = useState([]);
  const [courseSelect, setCourseSelect] = useState(null);
  const [bankList, setBankList] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isNewVerifyRequired, setIsNewVerifyRequired] = useState(false);
  const [validateListMenus, setValidateListMenus] = useState([]);

  // Refs
  const formRef = useRef();
  const topRef = useRef();
  const webcamRef = useRef(null);

  // Context
  const { userData, setUserData } = useContext(MainStore);

  // Webcam states
  const [showCamera, setShowCamera] = useState(false);
  const [facingMode, setFacingMode] = useState("environment");

  // Constants
  const re_validate_list = [
    "first_name",
    "last_name",
    "identity_number",
    "golf_course_id",
    "caddy_number",
  ];

  const validate_key = [
    { key: "phone_number", label: "หมายเลขโทรศัพท์" },
    { key: "bank_id", label: "ธนาคาร" },
    { key: "bank_user_name", label: "ชื่อบัญชีธนาคาร" },
    { key: "bank_number", label: "หมายเลขบัญชีธนาคาร" },
    { key: "price", label: "ทิปที่ต้องการ" },
    { key: "display_name", label: "ชื่อที่ใช้แสดงบนหน้าโปรไฟล์" },
    { key: "first_name", label: "ชื่อจริง" },
    { key: "last_name", label: "นามสกุล" },
    { key: "profile_picture_url", label: "รูปใส่ชุดแคดดี้" },
    { key: "identity_number", label: "หมายเลขบัตรประชาชน" },
    { key: "golf_course_id", label: "เลือกสนามกอล์ฟ" },
    { key: "caddy_number", label: "หมายเลขแคดดี้" },
    { key: "gender", label: "เพศ" },
  ];

  const loadInitialData = useCallback(async () => {
    setIsInitialLoading(true);
    try {
      const [banksResponse, golfCoursesResponse, controllerResponse] =
        await Promise.all([
          api.masterDataApi.getMasterDataBanks(),
          api.masterDataApi.getMasterDataGolfCourses(),
          api.masterDataApi.getMasterDataController(),
        ]);

      setBankList(banksResponse.data.data);
      setGolfCourseList(golfCoursesResponse.data.data);
      setControllerData(controllerResponse.data.data);

      if (userData.id) {
        await initialData(
          golfCoursesResponse.data.data,
          banksResponse.data.data
        );
      }
    } catch (err) {
      console.error("Load initial data error:", err);
      messageApi.error("เกิดข้อผิดพลาดในการโหลดข้อมูล");
    } finally {
      setIsInitialLoading(false);
    }
  }, [userData.id, messageApi]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const onChangeData = useCallback(
    async (obj, value, no_force_update = false) => {
      const new_data = { ...submitData, [obj]: value };
      const new_user_data = { ...user_data, [obj]: value };

      if (value === null || value === undefined) {
        delete new_data[obj];
        new_user_data[obj] = null;
      }

      if (fileList.length === 0) delete new_data["picture_list_image"];

      setSubmitData(new_data);
      // if (!no_force_update) setUserData(new_user_data);
    },
    [fileList.length, setUserData, submitData, user_data]
  );

  const onChange = useCallback(
    async ({ fileList: newFileList }) => {
      setIsImageUploading(true);
      try {
        const file_list_new = [];
        const file_list = [];

        for (const file of newFileList) {
          if (file.originFileObj) {
            const compressedFile = await compressImage(file.originFileObj);
            file_list.push(compressedFile);
          }
          file_list_new.push({ ...file, status: "done", percent: 100 });
        }

        setFileList(file_list_new);
        if (file_list.length > 0) {
          await onChangeData("picture_list_image", file_list);
        }
      } catch (error) {
        console.error("Error uploading images:", error);
        messageApi.error("เกิดข้อผิดพลาดในการอัพโหลดรูปภาพ");
      } finally {
        setIsImageUploading(false);
      }
    },
    [onChangeData, messageApi]
  );

  const compressImage = async (file) => {
    const options = {
      maxSizeMB: 0.25, // 300KB
      maxWidthOrHeight: 1280, // ลดขนาดด้านกว้าง/ยาวสูงสุด
      useWebWorker: true,
    };

    try {
      return await imageCompression(file, options);
    } catch (error) {
      console.error("Error compressing image:", error);
      return file; // หากบีบอัดล้มเหลว ใช้ไฟล์ต้นฉบับ
    }
  };

  const onPreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const base64ToFile = async (base64Data, fileName) => {
    const res = await fetch(base64Data);
    const blob = await res.blob();
    return new File([blob], fileName, { type: blob.type });
  };

  const capture = useCallback(async () => {
    setIsImageUploading(true);
    try {
      const screenshot = webcamRef.current.getScreenshot();
      const file = await base64ToFile(screenshot, "photo.jpg");
      setIdentityFile([file]);
      await onChangeData("identity_image", file);
      setShowCamera(false);
    } catch (error) {
      console.error("Capture error:", error);
      messageApi.error("เกิดข้อผิดพลาดในการถ่ายรูป");
    } finally {
      setIsImageUploading(false);
    }
  }, [onChangeData, messageApi]);

  const toggleCamera = () => {
    setFacingMode((prevFacingMode) =>
      prevFacingMode === "user" ? "environment" : "user"
    );
  };

  const setFormFields = useCallback((fields) => {
    setTimeout(() => {
      if (formRef?.current) {
        formRef.current.setFieldsValue(fields);
      }
    }, 100);
  }, []);

  async function initialData(golf_courses, banks) {
    const find_golf_course = golf_courses?.find(
      (golf_course) => golf_course?.id === user_data?.golf_course_id
    );
    const find_bank = banks?.find((bank) => bank?.id === user_data?.bank_id);

    let new_user_data = { ...user_data };
    let additional_list = [];

    // Set basic form data
    setFormFields({
      ...user_data,
      toggle_profile: user_data.is_show_profile_image,
    });

    setProfileFile({ image_url: user_profile?.image_url });
    user_data?.file_uploads?.forEach((file) => {
      switch (file.type) {
        case "identity":
          setIdentityFile([file]);
          new_user_data["identity_image"] = file;
          setFormFields({ identity_image: file });
          break;
        case "profile":
          setProfileFile(file);
          new_user_data["profile_picture_url"] = file;
          setFormFields({ profile_picture_url: file });
          break;
        case "additional":
          additional_list.push({
            uid: parseInt(file.id),
            name: file.file_name,
            status: "done",
            url: file.image_url,
          });
          break;
      }
    });

    // Set golf course
    if (find_golf_course) {
      setCourseSelect(find_golf_course);
      setFormFields({
        golf_course_id: `${find_golf_course.id} || ${find_golf_course.name}`,
      });
    }

    // Set bank
    if (find_bank) {
      setFormFields({
        bank_id: `${find_bank.id} || ${find_bank.name}`,
      });
      setSelectedBankName(find_bank.name);
    }

    setUserData(new_user_data);
    setIsSelectedBank(new_user_data.bank_id);
    setFileList(additional_list);
  }

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const uploadButton = (
    <button
      style={{
        border: 0,
        background: "none",
        width: "100%",
      }}
      type="button"
    >
      <PlusOutlined />
      <div
        style={{
          marginTop: 8,
        }}
      >
        เพิ่มรูป
      </div>
    </button>
  );

  async function checkReValidateData(temp_data) {
    let is_re_validate = false;

    Object.keys(temp_data).map((key) => {
      const find_re_validate = re_validate_list.find((value) => value === key);

      if (find_re_validate) {
        is_re_validate = true;
      }

      return is_re_validate;
    });

    return is_re_validate;
  }

  async function checkDataForValidate() {
    let data_checking = { ...user_data, ...submitData };

    let is_passed = true;

    let error_list = [];

    if (!controllerData.bypass_validation) {
      validate_key.map((validate) => {
        if (
          !data_checking[validate.key] ||
          data_checking[validate.key] === "null"
        ) {
          error_list.push(validate.label);
          is_passed = false;
        }

        return is_passed;
      });
    }

    if (is_passed) {
      setIsVerifyOpen(true);
    } else {
      setErrorVerifyList(error_list);
      setIsOpenVerifyError(true);
    }

    return is_passed;
  }

  const handleSubmit = useCallback(
    async (value, is_validate = false) => {
      const data = { ...user_data, ...submitData };
      let missingFields = [];

      const validateListMenus =
        PROFILE_MENUS[userData.user_type][currentPage].validate_value;

      validateListMenus.forEach((field) => {
        if (!data[field] || data[field] === "null") {
          missingFields.push(field);
        }
      });

      if (missingFields.length > 0) {
        setErrorVerifyList(
          missingFields.map((f) => validate_key.find((v) => v.key === f)?.label)
        );
        setIsOpenVerifyError(true);
        return;
      }

      if (isSubmitting) return;
      const isRequireVerify =
        is_validate || (isNewVerifyRequired && user_data?.register_state == 2);

      setIsSubmitting(true);
      try {
        const params = {
          ...user_data,
          ...submitData,
          ...(isRequireVerify && { isRequireVerify, register_state: 1 }),
        };

        const formData = new FormData();
        Object.keys(params).forEach((key) => {
          if (key !== "image_delete_list") {
            if (key === "picture_list_image") {
              params[key].forEach((pic) => {
                formData.append(key, pic);
              });
            } else {
              formData.append(key, params[key]);
            }
          } else {
            formData.append(key, JSON.stringify(params[key]));
          }
        });

        await api.userApi.updateProfile(formData);

        await onChangeData("picture_list_image", [], true);
        setSubmitData({});
        setUserData(params);
        messageApi.success("บันทึกข้อมูลสำเร็จแล้ว");
      } catch (error) {
        console.error("Submit error:", error);
        const messageTH = error?.response?.data?.error.messages?.th;
        if (messageTH) {
          messageApi.open({
            type: "warning",
            content: (
              <MessagePopup
                type="warning"
                message={messageTH}
                onClose={() => messageApi.destroy()}
              />
            ),
            duration: 0,
          });
        } else {
          messageApi.error(
            "บันทึกข้อมูลไม่สำเร็จ: " + (error.message || "เกิดข้อผิดพลาด")
          );
        }
      } finally {
        setIsVerifyOpen(false);
        setIsSubmitting(false);
        setIsReValidateOpen(false);
      }
    },
    [
      isSubmitting,
      submitData,
      user_data,
      isReValidateOpen,
      userData.user_type,
      onChangeData,
      setUserData,
      messageApi,
      currentPage,
    ]
  );

  function modalWebcam() {
    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 9999,
          backgroundColor: "rgba(0, 0, 0, 0.8)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100vh",
        }}
      >
        <div
          style={{
            marginTop: 10,
            width: "100%",
            position: "relative",
            zIndex: 999,
          }}
        >
          <Webcam
            audio={false}
            ref={webcamRef}
            // mirrored={true}
            screenshotFormat="image/jpeg"
            width="100%" // Set width to 100% for mobile responsiveness
            videoConstraints={{
              facingMode: facingMode, // Use rear camera on mobile if available
            }}
          />
        </div>
        <div
          style={{
            position: "absolute",
            bottom: 15,
            width: "100%",
            display: "flex",
            gap: 10,
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0px 20px",
          }}
        >
          <div />
          <div
            onClick={capture}
            style={{
              fontSize: "25px",
              marginTop: "10px",
              background: "#ffffff90",
              width: 60,
              height: 60,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CameraOutlined />
          </div>
          <div
            onClick={toggleCamera}
            style={{
              fontSize: "25px",
              marginTop: "10px",
              background: "#ffffff90",
              width: 60,
              height: 60,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ReloadOutlined />
          </div>
        </div>
        <div
          onClick={() => setShowCamera(false)}
          style={{
            fontSize: "25px",
            position: "absolute",
            right: 15,
            top: 5,
            marginTop: "10px",
            background: "#ffffff90",
            width: 60,
            height: 60,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CloseOutlined />
        </div>

        <div
          style={{
            position: "absolute",
            left: "35%",
            top: "50%",
            zIndex: 99,
            color: "white",
            display: "flex",
            fontSize: 18,
            fontWeight: "bold",
          }}
        >
          Loading Camera...
        </div>
      </div>
    );
  }

  function CaddyImageList() {
    return (
      <div
        style={{
          marginTop: parseInt(user_type) === 1 ? -5 : 15,
          background: "white",
          padding: 20,
          borderRadius: 20,
          boxShadow:
            "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
          marginBottom: 25,
        }}
      >
        <Form.Item
          name="picture_list"
          label={
            <div style={{ marginBottom: -5, fontSize: 18 }}>
              รูปภาพเพิ่มเติม
            </div>
          }
        >
          <Upload
            accept=".jpg,.jpeg,.png"
            beforeUpload={(file) => {
              const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
              const isAllowedType = allowedTypes.includes(file.type);

              if (!isAllowedType) {
                messageApi.error(
                  `${file.name} is not a valid file type. Only PNG, JPG, and JPEG are allowed.`
                );
              }

              return isAllowedType || Upload.LIST_IGNORE;
            }}
            listType="picture-card"
            fileList={fileList}
            style={{ height: 20 }}
            onChange={onChange}
            onPreview={onPreview}
            onRemove={(e) => {
              console.log("e", e);
              if (typeof e.uid === "number") {
                onChangeData("image_delete_list", [
                  ...(submitData.image_delete_list ?? []),
                  { delete_id: e.uid },
                ]);
              }
            }}
          >
            {fileList.length < 10 && "+ เพิ่มรูป"}
          </Upload>
        </Form.Item>
        {previewImage && (
          <Image
            wrapperStyle={{
              display: "none",
            }}
            preview={{
              visible: previewOpen,
              onVisibleChange: (visible) => setPreviewOpen(visible),
              afterOpenChange: (visible) => !visible && setPreviewImage(""),
            }}
            src={previewImage}
          />
        )}
      </div>
    );
  }

  function onClickSendMessage() {
    liff
      .sendMessages([
        {
          type: "text",
          text: "ติดต่อเจ้าหน้าที่ ต้องการ ใบหักภาษี ณ ที่จ่าย",
        },
      ])
      .then(() => {
        liff.closeWindow();
      })
      .catch((err) => {
        messageApi.error(`ส่งข้อความไปที่ Line ไม่สำเร็จ (${err})`);
      });
  }

  function modalConfirmSaved() {
    return (
      <Modal
        open={isReValidateOpen}
        centered
        closable={false}
        footer={false}
        onClose={() => setIsReValidateOpen(false)}
      >
        <center>
          <Image src={WarningIcon} width={75} style={{ marginBottom: 10 }} />
          <h2>มีการเปลี่ยนแปลงข้อมูลสำคัญ</h2>
          <div>
            เนื่องจากมีการเปลี่ยนแปลงข้อมูลส่วนตัวสำคัญ หากทำการยืนยัน Account
            ของคุณจะต้องรอการ Validate ใหม่จากทางผู้ดูแล
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
            <Button
              onClick={handleSubmit}
              htmlType="submit"
              type="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
              style={{
                width: "100%",
                height: 35,
                marginTop: 10,
                backgroundColor: "#009951",
                color: "white",
                fontSize: 14,
              }}
            >
              {isSubmitting ? "กำลังบันทึก..." : "ยืนยัน"}
            </Button>
            <Button
              onClick={() => setIsReValidateOpen(false)}
              ghost={true}
              style={{
                color: "red",
                borderColor: "red",
                width: "100%",
                height: 35,
                marginTop: 10,
                fontSize: 14,
              }}
            >
              ยกเลิก
            </Button>
          </div>
        </center>
      </Modal>
    );
  }

  function modalConfirmVerify() {
    return (
      <Modal
        open={isVerifyOpen}
        centered
        closable={false}
        footer={false}
        onClose={() => setIsVerifyOpen(false)}
      >
        <center>
          <Image src={VerifyIcon} width={75} style={{ marginBottom: 10 }} />
          <h2>ส่งยืนยันข้อมูล</h2>
          <div>คุณต้องการส่งยืนยันข้อมูลใช่หรือไม่ ?</div>
          <div>หากทำการยืนยันบัญชีของคุณจะต้องรอการการตรวจสอบจากทางผู้ดูแล</div>
          <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
            <Button
              onClick={() => handleSubmit(null, true)}
              htmlType="submit"
              type="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
              style={{
                width: "100%",
                height: 35,
                marginTop: 10,
                backgroundColor: "rgb(44 182 255)",
                color: "white",
                fontSize: 14,
              }}
            >
              {isSubmitting ? "กำลังส่ง..." : "ยืนยัน"}
            </Button>
            <Button
              onClick={() => setIsVerifyOpen(false)}
              ghost={true}
              style={{
                color: "red",
                borderColor: "red",
                width: "100%",
                height: 35,
                marginTop: 10,
                fontSize: 14,
              }}
            >
              ยกเลิก
            </Button>
          </div>
        </center>
      </Modal>
    );
  }

  function showErrorVerifyModal() {
    return (
      <Modal
        open={isOpenVerifyError}
        centered
        footer={false}
        closable={false}
        onCancel={() => setIsOpenVerifyError(false)}
      >
        <center>
          <h2 style={{ marginBottom: 0 }}>กรุณากรอกข้อมูลให้ครบถ้วน</h2>
          <h3>
            <div>กรุณากรอกข้อมูลที่มีด้านล่าง</div>
            <div>
              หรือในช่องที่มีคำว่า{" "}
              <span style={{ color: "red" }}>(จำเป็น)</span> ให้ครบถ้วน
            </div>
          </h3>
        </center>
        <div style={{ marginTop: 5 }}>
          {errorVerifyList?.map((err) => {
            return <div>- {err}</div>;
          })}
        </div>
        <Button
          onClick={() => setIsOpenVerifyError(false)}
          ghost
          style={{
            width: "100%",
            height: 40,
            marginTop: 15,
            border: "1px solid red",
            color: "red",
          }}
        >
          ปิด
        </Button>
      </Modal>
    );
  }

  if (isInitialLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <Spin
          size="large"
          indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
        />
        <div style={{ fontSize: 16, color: "#666" }}>กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  return (
    <div
      className={`profile-container ${
        user_type === 0 ? "profile-caddy-container" : "profile-golfer-container"
      }`}
      style={{ minHeight: "100vh" }}
    >
      {contextHolder}
      {showErrorVerifyModal()}
      {modalConfirmSaved()}
      {modalConfirmVerify()}
      {showCamera && modalWebcam()}

      {(isSubmitting || isImageUploading) && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            flexDirection: "column",
            gap: 16,
          }}
        >
          <Spin
            size="large"
            indicator={
              <LoadingOutlined style={{ fontSize: 48, color: "white" }} spin />
            }
          />
          <div style={{ color: "white", fontSize: 16 }}>
            {isSubmitting ? "กำลังบันทึกข้อมูล..." : "กำลังอัพโหลดรูปภาพ..."}
          </div>
        </div>
      )}
      <div
        style={{
          padding: 20,
          fontWeight: "bold",
          fontSize: 24,
          position: "sticky",
          top: 0,
          left: 0,
          zIndex: 9,
          background: "white",
          boxShadow:
            "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
          borderBottomRightRadius: 30,
          borderBottomLeftRadius: 30,
        }}
      >
        <center>โปรไฟล์{user_type === 0 ? "แคดดี้" : "นักกอล์ฟ"}</center>
        <center
          style={{
            fontSize: 14,
            fontWeight: "bold",
            marginTop: 5,
            color: "#777777",
          }}
        >
          {userData?.display_name}
        </center>
        <center
          style={{
            fontSize: 14,
            fontWeight: "bold",
            marginTop: 5,
            color: "#777777",
          }}
        >
          {"รหัสสมาชิก :"}
          {userData?.code}
        </center>
      </div>

      <div
        style={{
          padding: "10px 20px 0px 20px",
          position: "sticky",
          top: isClosed | (userData.register_state === 2) ? 100 : 10,
          left: 0,
          zIndex: 99,
          marginBottom: -20,
        }}
      >
        {(userData.register_state === 3) |
        (userData.register_state === 1) |
        (userData.register_state === 0) ? (
          <AlertError
            isClosed={isClosed}
            setIsClosed={setIsClosed}
            reject_reason={userData.reject_reason}
            register_state={userData.register_state}
          />
        ) : null}

        <div style={{ padding: "10px 0px 10px 0px" }}>
          <Segmented
            block
            onChange={(e) => {
              topRef?.current?.scrollIntoView({
                block: "center",
                behavior: "smooth",
              });

              setCurrentPage(e);
            }}
            style={{ width: "100%" }}
            options={PROFILE_MENUS[userData.user_type].map((menu) => {
              let is_required = false;
              if (user_data) {
                for (const key of menu.validate_value) {
                  if (
                    user_data[key] === null ||
                    user_data[key] === undefined ||
                    user_data[key] === ""
                  ) {
                    is_required = true;
                  }
                }
              }

              return {
                label: (
                  <div style={{ position: "relative" }}>
                    {is_required && (
                      <div
                        style={{
                          padding: 5,
                          background: "red",
                          borderRadius: "50%",
                          position: "absolute",
                          top: 3,
                          right: -7,
                        }}
                      ></div>
                    )}
                    {menu.label}
                  </div>
                ),
                value: menu.value,
              };
            })}
          />
        </div>
      </div>

      <div className="profile-content">
        <Form
          ref={formRef}
          onFinish={handleSubmit}
          layout="vertical"
          style={{ position: "relative", zIndex: 999 }}
        >
          <div ref={topRef} />
          {currentPage === 0 && (
            <div>
              <b style={{ fontSize: 18 }}>ข้อมูลโปรไฟล์</b>
              <div style={{ color: "red" }}>สำหรับแสดงผลที่หน้า Golfer</div>
              <Divider style={{ margin: "15px 0px" }} />
              <div style={{ position: "relative", padding: "0px 0px" }}>
                <Form.Item
                  style={{
                    width: "100%",
                    marginBottom: showProfile ? 20 : 20,
                    background: "white",
                    padding: 20,
                    borderRadius: 20,
                    boxShadow:
                      "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                  }}
                  name="profile_picture_url"
                  label={
                    <div style={{ marginBottom: -5, fontSize: 18 }}>
                      {parseInt(user_type) === 1
                        ? "รูปโปรไฟล์"
                        : "รูปใส่ชุดแคดดี้"}{" "}
                      <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                        (จำเป็น)
                      </span>
                    </div>
                  }
                  rules={[
                    {
                      required: false,
                      message: `กรุณา อัพโหลด${
                        parseInt(user_type) === 1
                          ? "รูปโปรไฟล์"
                          : "รูปใส่ชุดแคดดี้"
                      }}`,
                    },
                  ]}
                >
                  {profileFile?.image_url && profileFile.length !== 0 ? (
                    showProfile && parseInt(user_type) === 1 ? (
                      <div style={{ position: "relative" }}>
                        <Image
                          src={profileFile?.image_url}
                          style={{ borderRadius: 20, marginTop: 10 }}
                        />
                        <div
                          onClick={() => {
                            setProfileFile(null);
                            onChangeData("profile_picture_url", null);
                            formRef?.current?.setFieldsValue({
                              profile_picture_url: null,
                            });
                          }}
                          style={{
                            fontSize: 20,
                            width: 30,
                            height: 30,
                            borderRadius: "50%",
                            background: "#ffffff",
                            color: "red",
                            position: "absolute",
                            top: 20,
                            right: 10,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <CloseOutlined />
                        </div>
                      </div>
                    ) : (
                      parseInt(user_type) === 0 && (
                        <div style={{ position: "relative" }}>
                          <Image
                            src={profileFile?.image_url}
                            style={{ borderRadius: 20, marginTop: 10 }}
                          />
                          <div
                            onClick={() => {
                              setProfileFile(null);
                              onChangeData("profile_picture_url", null);
                              formRef?.current?.setFieldsValue({
                                profile_picture_url: null,
                              });
                            }}
                            style={{
                              fontSize: 20,
                              width: 30,
                              height: 30,
                              borderRadius: "50%",
                              background: "#ffffff",
                              color: "red",
                              position: "absolute",
                              top: 20,
                              right: 10,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <CloseOutlined />
                          </div>
                        </div>
                      )
                    )
                  ) : showProfile && parseInt(user_type) === 1 ? (
                    <Upload
                      accept=".jpg,.jpeg,.png"
                      beforeUpload={(file) => {
                        const allowedTypes = [
                          "image/png",
                          "image/jpeg",
                          "image/jpg",
                        ]; // Allowed MIME types
                        const isAllowedType = allowedTypes.includes(file.type);

                        if (!isAllowedType) {
                          messageApi.error(
                            `${file.name} is not a valid file type. Only PNG, JPG, and JPEG are allowed.`
                          );
                        }

                        return isAllowedType || Upload.LIST_IGNORE; // Reject unsupported files
                      }}
                      onChange={async (e) => {
                        const compressedFile = await compressImage(
                          e.fileList[0].originFileObj
                        );
                        setProfileFile({
                          image_url: await getBase64(compressedFile),
                        });
                        onChangeData("profile_picture_url", compressedFile);
                      }}
                      className="custom-antd-upload-profile avatar-uploader"
                      style={{ width: "100% !important" }}
                      listType="picture-card"
                    >
                      {uploadButton}
                    </Upload>
                  ) : (
                    parseInt(user_type) === 0 && (
                      <Upload
                        accept=".jpg,.jpeg,.png"
                        beforeUpload={async (file) => {
                          const allowedTypes = [
                            "image/png",
                            "image/jpeg",
                            "image/jpg",
                          ]; // Allowed MIME types
                          const isAllowedType = allowedTypes.includes(
                            file.type
                          );

                          if (!isAllowedType) {
                            messageApi.error(
                              `${file.name} is not a valid file type. Only PNG, JPG, and JPEG are allowed.`
                            );
                          }

                          const compressedFile = await compressImage(file);

                          setProfileFile({
                            image_url: await getBase64(compressedFile),
                          });
                          onChangeData("profile_picture_url", compressedFile);

                          return isAllowedType || Upload.LIST_IGNORE; // Reject unsupported files
                        }}
                        className="custom-antd-upload-profile avatar-uploader"
                        style={{ width: "100% !important" }}
                        listType="picture-card"
                      >
                        {uploadButton}
                      </Upload>
                    )
                  )}
                </Form.Item>
                {user_type === 0 && CaddyImageList()}
                {parseInt(user_type) === 1 && (
                  <Form.Item
                    name="toggle_profile"
                    style={{ position: "absolute", top: 20, right: 20 }}
                  >
                    <Switch
                      style={{ width: 130 }}
                      checked={submitData?.is_show_profile_image}
                      onChange={(e) => {
                        setShowProfile(e);
                        onChangeData("is_show_profile_image", e);
                      }}
                      checkedChildren="แสดงรูปภาพ"
                      unCheckedChildren="ไม่แสดงรูปภาพ"
                      defaultChecked={
                        submitData && submitData.is_show_profile_image
                          ? submitData.is_show_profile_image
                          : userData.is_show_profile_image
                      }
                      size="large"
                    />
                  </Form.Item>
                )}
                <Form.Item
                  style={{
                    marginTop: -5,
                    width: "100%",
                    background: "white",
                    padding: 20,
                    borderRadius: 20,
                    boxShadow:
                      "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                  }}
                  name="gender"
                  label={
                    <div style={{ marginBottom: -5, fontSize: 18 }}>
                      เพศ{" "}
                      {/* <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                        (จำเป็น)
                      </span> */}
                    </div>
                  }
                  initialValue={0}
                  onChange={(e) => onChangeData("gender", e.target.value)}
                  rules={[
                    {
                      required: false,
                      message: "กรุณาเลิือก `เพศ`",
                    },
                  ]}
                >
                  <Radio.Group>
                    {GENDER.map((gender) => {
                      return (
                        <Radio style={{ fontSize: 18 }} value={gender.value}>
                          {gender.label}
                        </Radio>
                      );
                    })}
                  </Radio.Group>
                </Form.Item>

                <Form.Item
                  style={{
                    marginTop: -5,
                    background: "white",
                    padding: 20,
                    borderRadius: 20,
                    boxShadow:
                      "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                  }}
                  name="display_name"
                  label={
                    <div style={{ marginBottom: -5, fontSize: 18 }}>
                      ชื่อที่ใช้แสดงบนหน้าโปรไฟล์{" "}
                      <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                        (จำเป็น)
                      </span>
                    </div>
                  }
                  onChange={(e) => onChangeData("display_name", e.target.value)}
                  rules={[
                    {
                      required: false,
                      message: "กรุณากรอก `ชื่อที่ใช้แสดงบนหน้าโปรไฟล์`",
                    },
                  ]}
                >
                  <Input
                    onChange={(e) => {
                      onChangeData("display_name", e.target.value);
                    }}
                  />
                </Form.Item>
                {user_type === 0 && (
                  <Form.Item
                    style={{
                      background: "white",
                      padding: 20,
                      borderRadius: 20,
                      boxShadow:
                        "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px;",
                    }}
                    name="about_me"
                    label={
                      <dv style={{ marginBottom: -5, fontSize: 18 }}>
                        เกี่ยวกับฉัน
                      </dv>
                    }
                  >
                    <TextArea
                      placeholder="ใส่ความสามารถในช่องนี้ เช่น ดูไลน์เก่ง เอนเตอร์เทนเก่ง"
                      autoSize={{ minRows: 3, maxRows: 3 }}
                      onChange={(e) => onChangeData("about_me", e.target.value)}
                    />
                  </Form.Item>
                )}
                {user_type === 0 && (
                  <Form.Item
                    style={{
                      marginTop: -5,
                      background: "white",
                      padding: 20,
                      borderRadius: 20,
                      boxShadow:
                        "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                    }}
                    name="price"
                    label={
                      <div style={{ marginBottom: -5, fontSize: 18 }}>
                        <div>
                          ทิปที่ต้องการ{" "}
                          <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                            (จำเป็น)
                          </span>
                        </div>
                        <div style={{ color: "#ff4d4f", fontSize: 12 }}>
                          สามารถแก้ไขได้ (ไม่เกิน{" "}
                          {new Intl.NumberFormat().format(
                            controllerData?.max_caddy_tips ?? 3000
                          )}
                          )
                        </div>
                      </div>
                    }
                  >
                    <InputNumber
                      min={0}
                      max={controllerData?.max_caddy_tips ?? 3000}
                      onChange={(e) => onChangeData("price", e)}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                )}
                {user_type === 0 && (
                  <Form.Item
                    style={{
                      marginTop: -5,
                      width: "100%",
                      background: "white",
                      padding: 20,
                      borderRadius: 20,
                      boxShadow:
                        "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                    }}
                    name="mode_preference"
                    label={
                      <div style={{ marginBottom: -5, fontSize: 18 }}>
                        ใช้รถหรือเดิน
                      </div>
                    }
                  >
                    <Select
                      placeholder="เลือกใช้รถหรือเดิน"
                      allowClear
                      onChange={(value) =>
                        onChangeData("mode_preference", value)
                      }
                      style={{ width: "100%" }}
                    >
                      {MODE_PREFERENCE.map((mode) => (
                        <Option key={mode.value} value={mode.value}>
                          {mode.label}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
              </div>
            </div>
          )}
          {currentPage === 1 && (
            <div>
              <b style={{ fontSize: 18 }}>ข้อมูลสนามกอล์ฟ</b>
              <Divider style={{ margin: "15px 0px" }} />
              <Form.Item
                style={{
                  background: "white",
                  padding: 20,
                  borderRadius: 20,
                  boxShadow:
                    "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                }}
                name="golf_course_id"
                label={
                  <div style={{ marginBottom: -5, fontSize: 18 }}>
                    เลือกสนามกอล์ฟ{" "}
                    <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                      (จำเป็น)
                    </span>
                  </div>
                }
                rules={[
                  { required: false, message: "กรุณาเลือก `เลือกสนามกอล์ฟ`" },
                ]}
              >
                <Select
                  placeholder="พิม์เพื่อค้นหาสนาม"
                  showSearch
                  onChange={(e) => {
                    const golfCourseId = e?.split(" || ")[0];
                    const isNewVerifyRequired =
                      golfCourseId !== user_data?.golf_course_id;
                    setIsNewVerifyRequired(isNewVerifyRequired);
                    setCourseSelect(
                      golfCourseList.find(
                        (golf_course) => golf_course?.id === golfCourseId
                      )
                    );
                    onChangeData("golf_course_id", golfCourseId);
                    formRef.current.setFieldsValue({ caddy_number: null });
                    setSubmitData((prev) => ({ ...prev, caddy_number: null }));
                  }}
                >
                  {golfCourseList?.map((golf_course) => {
                    return (
                      <Option
                        value={`${golf_course.id} || ${golf_course.name}`}
                      >
                        {golf_course.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
              {courseSelect && (
                <>
                  <Image
                    style={{
                      marginTop: -5,
                      borderRadius: 20,
                      background: "white",
                      padding: 20,
                      boxShadow:
                        "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                    }}
                    width={"100%"}
                    // height={200}
                    src={courseSelect?.image_url}
                    preview={false}
                    fallback={DefaultGolfCourseImage}
                  />
                  <Form.Item
                    style={{
                      marginTop: 10,
                      background: "white",
                      padding: 20,
                      borderRadius: 20,
                      boxShadow:
                        "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                    }}
                    required={true}
                    name="caddy_number"
                    label={
                      <div style={{ marginBottom: -5, fontSize: 18 }}>
                        หมายเลขแคดดี้{" "}
                        <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                          (จำเป็น)
                        </span>
                      </div>
                    }
                    rules={[
                      { required: false, message: "กรุณากรอก `หมายเลขแคดดี้`" },
                    ]}
                  >
                    <Input
                      onChange={(e) => {
                        onChangeData("caddy_number", e.target.value);
                      }}
                    />
                  </Form.Item>
                </>
              )}
            </div>
          )}
          {currentPage === 2 && (
            <div>
              <b style={{ fontSize: 18 }}>ข้อมูลช่องทางการรับเงิน</b>
              <Divider style={{ margin: "15px 0px" }} />
              <Form.Item
                style={{
                  background: "white",
                  padding: 20,
                  borderRadius: 20,
                  boxShadow:
                    "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                }}
                name="bank_id"
                label={
                  <div style={{ marginBottom: -5, fontSize: 18 }}>
                    เลือกบัญชี{" "}
                    <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                      (จำเป็น)
                    </span>
                  </div>
                }
                rules={[{ required: false, message: "กรุณาเลือก `บัญชี`" }]}
              >
                <Select
                  onChange={(e) => {
                    setIsSelectedBank(e);
                    onChangeData("bank_id", e?.split(" || ")[0]);
                    setSelectedBankName(e?.split(" || ")[1]);
                    formRef.current.setFieldsValue({
                      bank_user_name: null,
                      bank_number: null,
                    });
                  }}
                >
                  {bankList?.map((bank) => {
                    return (
                      <Option value={`${bank.id} || ${bank.name}`}>
                        {bank.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>

              {isSelectedBank && (
                <>
                  <Form.Item
                    style={{
                      marginTop: -5,
                      width: "100%",
                      background: "white",
                      padding: 20,
                      borderRadius: 20,
                      boxShadow:
                        "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                    }}
                    name="bank_user_name"
                    label={
                      <div style={{ marginBottom: -5, fontSize: 18 }}>
                        ชื่อบัญชี {selectedBankName}{" "}
                        <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                          (จำเป็น)
                        </span>
                      </div>
                    }
                    rules={[
                      { required: false, message: "กรุณากรอก `ชื่อบัญชี`" },
                    ]}
                  >
                    <Input
                      placeholder="ชื่อ นามสกุล"
                      onChange={(e) =>
                        onChangeData("bank_user_name", e.target.value)
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    style={{
                      marginTop: -5,
                      width: "100%",
                      background: "white",
                      padding: 20,
                      borderRadius: 20,
                      boxShadow:
                        "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                    }}
                    name="bank_number"
                    label={
                      <div style={{ marginBottom: -5, fontSize: 18 }}>
                        เลขบัญชี {selectedBankName}{" "}
                        <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                          (จำเป็น)
                        </span>
                      </div>
                    }
                    rules={[
                      {
                        required: false,
                        message: "กรุณากรอก `เลขบัญชีธนาคาร`",
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      placeholder={`${
                        selectedBankName === "พร้อมเพย์"
                          ? "หมายโทรศัพท์มือถือ / บัตรประชาชน"
                          : "หมายเลขบัญชีธนาคาร"
                      }`}
                      onChange={(e) => {
                        const { value: inputValue } = e.target;
                        const reg = /^\d*$/;

                        if (reg.test(inputValue)) {
                          onChangeData("bank_number", inputValue);
                          formRef?.current?.setFieldsValue({
                            bank_number: inputValue,
                          });
                        } else {
                          formRef?.current?.setFieldsValue({
                            bank_number: submitData.bank_number,
                          });
                        }
                      }}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                  <Form.Item
                    style={{
                      marginTop: -5,
                      background: "white",
                      padding: 20,
                      borderRadius: 20,
                      boxShadow:
                        "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                    }}
                    name="identity_number"
                    label={
                      <div style={{ marginBottom: -5, fontSize: 18 }}>
                        หมายเลขบัตรประชาชน
                        <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                          {" "}
                          (จำเป็น)
                        </span>
                      </div>
                    }
                    rules={[
                      {
                        required: false,
                        message: "กรุณากรอก `หมายเลขบัตรประชาชน`",
                      },
                    ]}
                  >
                    <Input
                      type="number"
                      onChange={(e) => {
                        const { value: inputValue } = e.target;
                        const reg = /^\d*$/;
                        if (reg.test(inputValue) && inputValue.length <= 13) {
                          onChangeData("identity_number", inputValue);
                          formRef?.current?.setFieldsValue({
                            identity_number: inputValue,
                          });
                        } else {
                          formRef?.current?.setFieldsValue({
                            identity_number: submitData.identity_number,
                          });
                        }
                      }}
                      count={{
                        show: true,
                        max: 13,
                        min: 13,
                      }}
                      style={{ width: "100%" }}
                      maxLength={13}
                    />
                  </Form.Item>
                </>
              )}
            </div>
          )}
          {currentPage === 3 && (
            <div>
              <b style={{ fontSize: 18 }}>ข้อมูลส่วนตัว</b>
              <Divider style={{ margin: "15px 0px" }} />
              <div style={{ position: "relative", marginTop: 10 }}>
                <Form.Item
                  style={{
                    marginTop: -5,
                    background: "white",
                    padding: 20,
                    borderRadius: 20,
                    boxShadow:
                      "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                  }}
                  name="code"
                  label={
                    <div style={{ marginBottom: -5, fontSize: 18 }}>
                      {"รหัสสมาชิก"}
                    </div>
                  }
                  onChange={(e) => onChangeData("code", e.target.value)}
                >
                  <Input
                    disabled={true}
                    onChange={(e) => {
                      onChangeData("code", e.target.value);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  style={{
                    background: "white",
                    padding: 20,
                    borderRadius: 20,
                    boxShadow:
                      "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                  }}
                  name="first_name"
                  label={
                    <div style={{ marginBottom: -15, fontSize: 18 }}>
                      ชื่อจริง{" "}
                      {userData?.user_type === 0 && (
                        <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                          (จำเป็น)
                        </span>
                      )}
                    </div>
                  }
                  rules={[
                    {
                      required: false,
                      message: "กรุณากรอก `ชื่อจริง`",
                    },
                  ]}
                >
                  <Input
                    placeholder="ชื่อจริง"
                    onChange={(e) => {
                      onChangeData("first_name", e.target.value);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="last_name"
                  style={{
                    marginTop: -10,
                    background: "white",
                    padding: 20,
                    borderRadius: 20,
                    boxShadow:
                      "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                  }}
                  label={
                    <div style={{ marginBottom: -15, fontSize: 18 }}>
                      นามสกุล{" "}
                      {userData?.user_type === 0 && (
                        <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                          (จำเป็น)
                        </span>
                      )}
                    </div>
                  }
                  rules={[
                    {
                      required: false,
                      message: "กรุณากรอก `นามสกุล`",
                    },
                  ]}
                >
                  <Input
                    placeholder="นามสกุล"
                    onChange={(e) => {
                      onChangeData("last_name", e.target.value);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  style={{
                    marginTop: -5,
                    background: "white",
                    padding: 20,
                    borderRadius: 20,
                    boxShadow:
                      "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                  }}
                  name="phone_number"
                  label={
                    <div style={{ marginBottom: -5, fontSize: 18 }}>
                      เบอร์โทรศัพท์{" "}
                      <span style={{ color: "#ff4d4f", fontSize: 12 }}>
                        (จำเป็น)
                      </span>
                    </div>
                  }
                  onChange={(e) => onChangeData("phone_number", e.target.value)}
                  rules={[
                    {
                      required: false,
                      message: "กรุณากรอก `เบอร์โทรศัพท์`",
                    },
                    {
                      pattern: phoneNumberPattern,
                      message: "กรุณากรอก `เบอร์โทรศัพท์` ให้ถูกต้อง",
                    },
                  ]}
                >
                  <Input
                    maxLength={10}
                    onChange={(e) => {
                      e.target.value = formatPhoneNumber(e.target.value);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  rules={[
                    { type: "email", message: "กรุณากรอก `อีเมล์` ให้ถูกต้อง" },
                  ]}
                  name="email"
                  style={{
                    marginTop: -10,
                    background: "white",
                    padding: 20,
                    borderRadius: 20,
                    boxShadow:
                      "rgba(0, 0, 0, 0.12) 0px 1px 3px, rgba(0, 0, 0, 0.24) 0px 1px 2px",
                  }}
                  label={
                    <div style={{ marginBottom: -15, fontSize: 18 }}>
                      อีเมล์
                    </div>
                  }
                  onChange={(e) => onChangeData("email", e.target.value)}
                >
                  <Input
                    placeholder="อีเมล์"
                    onChange={(e) => {
                      onChangeData("email", e.target.value);
                    }}
                  />
                </Form.Item>

                {user_type === 0 && (
                  <>
                    <div
                      onClick={onClickSendMessage}
                      style={{
                        cursor: "pointer",
                        fontSize: 16,
                        backgroundColor: "#fcffc5",
                        padding: 10,
                        width: "100%",
                        margin: "15px 0px",
                        borderRadius: 20,
                        textAlign: "center",
                        textDecoration: "underline",
                        textUnderlineOffset: 4,
                      }}
                    >
                      หากต้องการใบหักภาษี ณ ที่จ่าย "คลิกที่นี่"
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          <Form.Item style={{ alignSelf: "end" }}>
            <Button
              htmlType="submit"
              type="primary"
              loading={isSubmitting}
              disabled={isSubmitting || isImageUploading}
              style={{
                width: "100%",
                height: 50,
                marginTop: 10,
                backgroundColor: "#009951",
                color: "white",
                fontSize: 18,
              }}
            >
              <div>{isSubmitting ? "กำลังบันทึก..." : "บันทึกข้อมูล"}</div>
            </Button>
          </Form.Item>
        </Form>

        {[0, 3].includes(user_data.register_state) && (
          <Button
            onClick={checkDataForValidate}
            type="primary"
            disabled={isSubmitting || isImageUploading}
            style={{
              zIndex: 9,
              width: "100%",
              height: 50,
              marginTop: 0,
              backgroundColor: "rgb(44 182 255)",
              color: "white",
              fontSize: 18,
            }}
          >
            <div>ส่งยืนยันข้อมูล</div>
          </Button>
        )}

        <div
          style={{
            position: "fixed",
            zIndex: 0,
            bottom: 0,
            left: 0,
            opacity: 0.5,
          }}
        >
          <Image
            width="100%"
            height={150}
            preview={false}
            src={BGImage}
            style={{
              filter:
                "brightness(0) saturate(100%) invert(77%) sepia(67%) saturate(395%) hue-rotate(87deg) brightness(91%) contrast(85%)",
            }}
          />
        </div>
      </div>
    </div>
  );
}
